import type { InvokeHttpRouteProps } from '../types';
import {
  buildRequestHeaders,
  buildRouteUrl,
  safeJsonParse,
  type NormalizeApiError,
} from './httpRequest';

interface CreateHttpRouteInvokerProps {
  apiBaseUrl: string;
  getIdToken: () => Promise<string | undefined>;
  normalizeApiError: NormalizeApiError;
}

export const createHttpRouteInvoker =
  ({
    apiBaseUrl,
    getIdToken,
    normalizeApiError,
  }: CreateHttpRouteInvokerProps) =>
  async <TResponse>(props: InvokeHttpRouteProps): Promise<TResponse> => {
    const {
      method,
      path,
      params,
      query,
      headers: customHeaders,
      body,
      requestData,
    } = props;
    const hasMappedBody = 'body' in props;
    const requestBody = hasMappedBody ? body : requestData;
    const shouldSendBody =
      method !== 'GET' && (method !== 'DELETE' || hasMappedBody);
    const shouldSendJsonContentType = method !== 'DELETE' || shouldSendBody;
    const headers = await buildRequestHeaders({
      getIdToken,
      customHeaders,
      includeJsonContentType: shouldSendJsonContentType,
    });

    const response = await fetch(
      buildRouteUrl(apiBaseUrl, path, params, query),
      {
        method,
        credentials: 'include',
        headers,
        body: shouldSendBody
          ? JSON.stringify(requestBody == null ? {} : requestBody)
          : undefined,
      },
    );

    const payload = await safeJsonParse(response);

    if (!response.ok) {
      throw normalizeApiError({
        payload,
        statusCode: response.status,
        fallbackMessage: `Request failed with status ${response.status}`,
      });
    }

    return payload as TResponse;
  };
